import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { debugRecorder } from '../../services/debugRecorder';
import { generateTraceId } from '../../utils/trace';
import { api, API_URL } from '../../api/client';

// Async thunk for submitting debug report
export const submitDebugReport = createAsyncThunk(
  'debug/submitReport',
  async ({ description, issueNumber }, { rejectWithValue, getState }) => {
    try {
      // Get the last recorded session from IndexedDB
      const session = await debugRecorder.getLastSession();

      if (!session) {
        throw new Error('No debug session found');
      }

      // Get current user for god mode check
      const { auth } = getState();
      if (!auth.user?.isGodMode) {
        throw new Error('God mode required to submit debug reports');
      }

      // Extract only FAILED network calls (errors, 4xx, 5xx) - these are what we need for debugging
      const failedNetworkCalls = (session.networkCalls || [])
        .filter(call => call.status >= 400 || call.error)
        .slice(-10)
        .map(call => ({
          url: call.url,
          method: call.method,
          status: call.status,
          error: call.error,
          timestamp: call.timestamp,
          // Only include response body for errors, truncated
          responseBody: typeof call.responseBody === 'string'
            ? call.responseBody.slice(0, 300)
            : JSON.stringify(call.responseBody)?.slice(0, 300),
        }));

      // Extract only ERROR console output
      const errorConsoleOutput = (session.consoleOutput || [])
        .filter(output => output.type === 'error' || output.type === 'warn')
        .slice(-20)
        .map(output => ({
          type: output.type,
          timestamp: output.timestamp,
          message: output.args?.join(' ').slice(0, 500) || '',
        }));

      // Keep only meaningful events (clicks, navigation, errors) - skip focus/blur noise
      const meaningfulEvents = (session.events || [])
        .filter(e => ['click', 'navigation', 'error', 'submit', 'change'].includes(e.type))
        .slice(-50)
        .map(e => ({
          type: e.type,
          timestamp: e.timestamp,
          url: e.url,
          target: e.target ? {
            tagName: e.target.tagName,
            text: e.target.text?.slice(0, 50),
            id: e.target.id,
          } : null,
        }));

      // Prepare a MINIMAL debug payload - focus on what helps diagnose issues
      // Parse issueNumber as integer (Go expects float64 from JSON, not string)
      const parsedIssueNumber = issueNumber ? parseInt(issueNumber, 10) : null;
      const debugData = {
        description,
        issueNumber: parsedIssueNumber && !isNaN(parsedIssueNumber) ? parsedIssueNumber : null,
        session: {
          metadata: {
            startTime: session.metadata?.startTime,
            url: session.metadata?.url,
            viewport: session.metadata?.viewport,
          },
          // Only include what's useful for debugging
          events: meaningfulEvents,
          failedNetworkCalls,
          consoleErrors: errorConsoleOutput,
          // No state snapshots - they're huge and not needed
        },
        environment: {
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: Date.now(),
        },
        userEmail: auth.user.email || 'unknown',
      };

      // Submit as a debug job through the existing job API
      const jobPayload = {
        idempotency_key: `debug-${Date.now()}-${generateTraceId()}`,
        job_type: {
          name: 'debug-issue',
          version: '1.0.0',
        },
        inputs: debugData,
        hints: {
          priority: 'low',
          staging_only: true,
        },
      };

      // Check payload size - Temporal has a 2MB limit
      const payloadSize = JSON.stringify(jobPayload).length;
      console.log(`[Debug] Payload size: ${(payloadSize / 1024).toFixed(1)}KB`);
      if (payloadSize > 1500000) {
        console.warn(`[Debug] Payload too large (${(payloadSize / 1024 / 1024).toFixed(2)}MB), may fail submission`);
      }

      // Use the existing API client to submit the job to the gateway
      const response = await api.post(`${API_URL}/api/v1/jobs`, jobPayload, { traceId: generateTraceId() });

      // Clear the session after successful submission
      await debugRecorder.clearSessions();

      // Return job info
      return {
        success: true,
        jobId: response.job_id,
        message: issueNumber
          ? `Debug report submitted as comment to issue #${issueNumber}`
          : 'Debug report submitted, issue will be created shortly',
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const debugSlice = createSlice({
  name: 'debug',
  initialState: {
    isRecording: false,
    sessionStartTime: null,
    eventCount: 0,
    lastError: null,
    isSubmitting: false,
    submissionProgress: 0,
    lastSubmission: null,
  },
  reducers: {
    startRecording: (state) => {
      state.isRecording = true;
      state.sessionStartTime = Date.now();
      state.eventCount = 0;
      state.lastError = null;
    },
    stopRecording: (state) => {
      state.isRecording = false;
    },
    updateEventCount: (state, action) => {
      state.eventCount = action.payload;
    },
    setError: (state, action) => {
      state.lastError = action.payload;
    },
    clearError: (state) => {
      state.lastError = null;
    },
    updateSubmissionProgress: (state, action) => {
      state.submissionProgress = action.payload;
    },
    resetDebugState: (state) => {
      state.isRecording = false;
      state.sessionStartTime = null;
      state.eventCount = 0;
      state.lastError = null;
      state.submissionProgress = 0;
    },
  },
  extraReducers: (builder) => {
    builder
      // submitDebugReport
      .addCase(submitDebugReport.pending, (state) => {
        state.isSubmitting = true;
        state.lastError = null;
        state.submissionProgress = 0;
      })
      .addCase(submitDebugReport.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.submissionProgress = 100;
        state.lastSubmission = {
          timestamp: Date.now(),
          jobId: action.payload.jobId,
          message: action.payload.message,
        };
        // Reset recording state after successful submission
        state.eventCount = 0;
        state.sessionStartTime = null;
      })
      .addCase(submitDebugReport.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submissionProgress = 0;
        state.lastError = action.payload;
      });
  },
});

export const {
  startRecording,
  stopRecording,
  updateEventCount,
  setError,
  clearError,
  updateSubmissionProgress,
  resetDebugState,
} = debugSlice.actions;

export default debugSlice.reducer;